/**
 * Organization-specific localStorage wrapper
 * 
 * This module provides a wrapper around localStorage that automatically
 * prefixes all keys with the current organization ID.
 * 
 * Example:
 * - Without prefix: localStorage.setItem('filters', '...')
 * - With prefix (org 123): localStorage.setItem('org_123_filters', '...')
 * 
 * This allows multiple organizations to have separate data without conflicts.
 */

let currentEntityId = null;
const SYSTEM_KEYS = ['theme', 'language']; // Keys that should NOT be prefixed

/**
 * Set the current entity ID for all subsequent localStorage operations
 * @param {number|string} entityId - The entity/organization ID
 */
export function setCurrentEntityId(entityId) {
    currentEntityId = entityId;
    console.log(`Organization localStorage prefix set to: org_${entityId}_`);
}

/**
 * Get the current entity ID
 * @returns {number|string|null} Current entity ID
 */
export function getCurrentEntityId() {
    return currentEntityId;
}

/**
 * Check if a key should be prefixed
 * @param {string} key - The localStorage key
 * @returns {boolean}
 */
function shouldPrefixKey(key) {
    // Don't prefix system keys
    if (SYSTEM_KEYS.includes(key)) {
        return false;
    }
    // Don't prefix keys that are already prefixed
    if (key.startsWith('org_')) {
        return false;
    }
    return true;
}

/**
 * Get the prefixed key for the current organization
 * @param {string} key - The original key
 * @returns {string} The prefixed key
 */
function getPrefixedKey(key, { warnIfUnscoped = false } = {}) {
    if (!shouldPrefixKey(key)) {
        return key;
    }

    if (!currentEntityId) {
        if (warnIfUnscoped) {
            console.warn(`orgStorage: No entity ID set, using unprefixed key "${key}"`);
        }
        return key;
    }

    return `org_${currentEntityId}_${key}`;
}

/**
 * Organization-aware localStorage wrapper
 * All methods automatically prefix keys with the current organization ID
 */
export const orgStorage = {
    /**
     * Get an item from localStorage
     * @param {string} key - The key to retrieve
     * @returns {string|null} The stored value
     */
    getItem(key) {
        const prefixedKey = getPrefixedKey(key);
        return localStorage.getItem(prefixedKey);
    },

    /**
     * Set an item in localStorage
     * @param {string} key - The key to store
     * @param {string} value - The value to store
     */
    setItem(key, value) {
        const prefixedKey = getPrefixedKey(key, { warnIfUnscoped: true });
        localStorage.setItem(prefixedKey, value);
    },

    /**
     * Remove an item from localStorage
     * @param {string} key - The key to remove
     */
    removeItem(key) {
        const prefixedKey = getPrefixedKey(key);
        localStorage.removeItem(prefixedKey);
    },

    /**
     * Clear all items for the current organization
     * System keys (theme, language) are preserved
     */
    clear() {
        if (!currentEntityId) {
            console.warn('orgStorage.clear(): No entity ID set');
            return;
        }

        const prefix = `org_${currentEntityId}_`;
        const keysToRemove = [];

        // Find all keys for this organization
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                keysToRemove.push(key);
            }
        }

        // Remove them
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`Cleared ${keysToRemove.length} keys for organization ${currentEntityId}`);
    },

    /**
     * Get the number of items for the current organization
     * @returns {number}
     */
    get length() {
        if (!currentEntityId) {
            return 0;
        }

        const prefix = `org_${currentEntityId}_`;
        let count = 0;

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                count++;
            }
        }

        return count;
    },

    /**
     * Get a key by index for the current organization
     * @param {number} index - The index
     * @returns {string|null}
     */
    key(index) {
        if (!currentEntityId) {
            return null;
        }

        const prefix = `org_${currentEntityId}_`;
        let count = 0;

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                if (count === index) {
                    // Return the key without prefix
                    return key.replace(prefix, '');
                }
                count++;
            }
        }

        return null;
    },

    /**
     * Check if a key exists for the current organization
     * @param {string} key - The key to check
     * @returns {boolean}
     */
    hasItem(key) {
        const prefixedKey = getPrefixedKey(key);
        return localStorage.getItem(prefixedKey) !== null;
    },
};

/**
 * Get all keys for a specific organization
 * @param {number|string} entityId - The entity ID
 * @returns {Array<string>} Array of keys (without prefix)
 */
export function getOrgKeys(entityId) {
    const prefix = `org_${entityId}_`;
    const keys = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keys.push(key.replace(prefix, ''));
        }
    }

    return keys;
}

/**
 * Get all organizations that have data in localStorage
 * @returns {Array<string>} Array of entity IDs
 */
export function getAllOrganizations() {
    const orgs = new Set();
    const orgPrefix = 'org_';

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(orgPrefix)) {
            // Extract entity ID from key like "org_123_something"
            const match = key.match(/^org_(\d+)_/);
            if (match) {
                orgs.add(match[1]);
            }
        }
    }

    return Array.from(orgs);
}

/**
 * Delete all data for a specific organization
 * @param {number|string} entityId - The entity ID
 */
export function deleteOrgData(entityId) {
    const prefix = `org_${entityId}_`;
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`Deleted ${keysToRemove.length} keys for organization ${entityId}`);
}

/**
 * Copy all data from one organization to another
 * @param {number|string} sourceEntityId - Source organization ID
 * @param {number|string} targetEntityId - Target organization ID
 */
export function copyOrgData(sourceEntityId, targetEntityId) {
    const sourcePrefix = `org_${sourceEntityId}_`;
    const targetPrefix = `org_${targetEntityId}_`;
    let count = 0;

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(sourcePrefix)) {
            const value = localStorage.getItem(key);
            const newKey = key.replace(sourcePrefix, targetPrefix);
            localStorage.setItem(newKey, value);
            count++;
        }
    }

    console.log(`Copied ${count} keys from organization ${sourceEntityId} to ${targetEntityId}`);
}

// Export default for convenience
export default {
    setCurrentEntityId,
    getCurrentEntityId,
    orgStorage,
    getOrgKeys,
    getAllOrganizations,
    deleteOrgData,
    copyOrgData,
};
